// Per-store cart, persisted in localStorage under `cdm_cart_<username>`.
// No context/provider needed — any component calls useStorefrontCart(username)
// and stays in sync via a same-tab CustomEvent (other components on this
// page) and the native `storage` event (other tabs).
import { useCallback, useEffect, useState } from "react";

const keyFor = (username) => `cdm_cart_${(username || "").toLowerCase()}`;
const eventFor = (username) => `cdm_cart_updated_${(username || "").toLowerCase()}`;

function readCart(username) {
  try {
    const raw = localStorage.getItem(keyFor(username));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeCart(username, items) {
  try {
    localStorage.setItem(keyFor(username), JSON.stringify(items));
  } catch {
    // storage full/unavailable — cart just won't persist this time
  }
  window.dispatchEvent(new CustomEvent(eventFor(username), { detail: items }));
}

// Two lines are the "same" line if same product AND same size — a
// product with sizes can have separate cart lines per size.
const sameLine = (a, b) => a.catalog_item_id === b.catalog_item_id && (a.size || null) === (b.size || null);

export function useStorefrontCart(username) {
  const [items, setItems] = useState(() => readCart(username));

  useEffect(() => {
    setItems(readCart(username));
    const onLocal = (e) => setItems(e.detail || readCart(username));
    const onStorage = (e) => {
      if (e.key === keyFor(username)) setItems(readCart(username));
    };
    window.addEventListener(eventFor(username), onLocal);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(eventFor(username), onLocal);
      window.removeEventListener("storage", onStorage);
    };
  }, [username]);

  const addItem = useCallback(
    (line) => {
      const current = readCart(username);
      const idx = current.findIndex((l) => sameLine(l, line));
      const next =
        idx >= 0
          ? current.map((l, i) => (i === idx ? { ...l, quantity: l.is_digital ? 1 : l.quantity + line.quantity } : l))
          : [...current, line];
      writeCart(username, next);
    },
    [username]
  );

  const updateQuantity = useCallback(
    (catalog_item_id, size, quantity) => {
      const current = readCart(username);
      const next =
        quantity <= 0
          ? current.filter((l) => !sameLine(l, { catalog_item_id, size }))
          : current.map((l) => (sameLine(l, { catalog_item_id, size }) ? { ...l, quantity: l.is_digital ? 1 : quantity } : l));
      writeCart(username, next);
    },
    [username]
  );

  const removeItem = useCallback(
    (catalog_item_id, size) => {
      const current = readCart(username);
      writeCart(username, current.filter((l) => !sameLine(l, { catalog_item_id, size })));
    },
    [username]
  );

  const clear = useCallback(() => writeCart(username, []), [username]);

  const count = items.reduce((s, l) => s + l.quantity, 0);
  const subtotal = items.reduce((s, l) => s + Number(l.price) * l.quantity, 0);
  const hasPhysical = items.some((l) => !l.is_digital);

  return { items, addItem, updateQuantity, removeItem, clear, count, subtotal, hasPhysical };
}

// Saved customer details for prefill on a return visit, same browser only —
// there's no customer login, so this is the only "remember me" we can do
// without a new, unauthenticated lookup-by-phone endpoint (see note below).
// Only written after a successful order; never includes payment details.
const CUSTOMER_KEY = "cdm_storefront_customer";

export function getSavedCustomer() {
  try {
    const raw = localStorage.getItem(CUSTOMER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function saveCustomer(customer) {
  try {
    localStorage.setItem(CUSTOMER_KEY, JSON.stringify(customer));
  } catch {
    // ignore — prefill just won't work next time
  }
}